import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "common/locales";
import useConfirmation from "common/utils/useConfirmation";
import { Farm, useDeleteFarm, useUpdateFarm, useUserFarms } from "model";
import { MouseEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { switchFarm } from "services";
import { useAppStore } from "stores";
import { AnalyticsEvents, mixpanel } from "../../common/analytics";

interface FarmMenuProps {
  farm: Farm;
}

export default function FarmMenu({ farm }: FarmMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectFarmDialogOpen, setSelectFarmDialogOpen] = useState(false);
  const [editedName, setEditedName] = useState(farm.name);
  const [isDeleting, setIsDeleting] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: allFarms = [] } = useUserFarms();
  const user = useAppStore((state) => state.user);
  const isOwner = farm.owner === user?.guid;

  // Use React Query mutations
  const updateFarmMutation = useUpdateFarm();
  const deleteFarmMutation = useDeleteFarm();

  const confirmDeleteFarm = useConfirmation({
    title: t("confirmationdeletedialog.farm.title"),
    description: t("confirmationdeletedialog.farm.description", {
      name: farm.name,
    }),
  });

  const otherFarms = allFarms.filter((f) => f.guid !== farm.guid);

  return (
    <div>
      <IconButton
        edge="end"
        aria-haspopup="true"
        onClick={openMenu}
        size="small"
        sx={{
          backgroundColor: "#F1EFF7",
          color: "#7D5DA3",
          "&:hover": { backgroundColor: "#E5E1F0" },
        }}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        id="farm-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={closeMenu}
      >
        <MenuItem onClick={handleEditClick}>{t("farm.menu.edit")}</MenuItem>
        {isOwner && (
          <MenuItem onClick={handleDeleteClick}>
            {t("farm.menu.delete")}
          </MenuItem>
        )}
      </Menu>

      {/* Edit Farm Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("farm.edit.title")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("farm.edit.nameLabel")}
            fullWidth
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            {t("edit.button.back")}
          </Button>
          <Button onClick={handleSaveEdit} variant="contained">
            {t("edit.button.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Select Farm Dialog (shown when deleting and there are other farms) */}
      <Dialog
        open={selectFarmDialogOpen}
        onClose={() => !isDeleting && setSelectFarmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("farm.delete.selectFarm.title")}</DialogTitle>
        <DialogContent>
          {isDeleting ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 3,
              }}
            >
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>{t("farm.delete.inProgress")}</Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ mb: 2 }}>
                {t("farm.delete.selectFarm.description", { name: farm.name })}
              </Typography>
              {otherFarms.map((otherFarm) => (
                <Button
                  key={otherFarm.guid}
                  fullWidth
                  variant="outlined"
                  sx={{ mb: 1, justifyContent: "flex-start" }}
                  onClick={() => handleSelectFarmAndDelete(otherFarm.guid)}
                >
                  {otherFarm.name}
                </Button>
              ))}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  function openMenu(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function closeMenu() {
    setAnchorEl(null);
  }

  function handleEditClick() {
    closeMenu();
    setEditedName(farm.name);
    setEditDialogOpen(true);
  }

  async function handleSaveEdit() {
    if (editedName.trim() && editedName !== farm.name) {
      await updateFarmMutation.mutateAsync({
        ...farm,
        name: editedName.trim(),
      });
      mixpanel.track(AnalyticsEvents.FARM_UPDATED);
    }
    setEditDialogOpen(false);
  }

  async function handleDeleteClick() {
    closeMenu();

    if (!isOwner) {
      return;
    }

    if (otherFarms.length > 0) {
      setSelectFarmDialogOpen(true);
    } else {
      const confirmed = await confirmDeleteFarm();
      if (confirmed) {
        await performDelete();
      }
    }
  }

  async function handleSelectFarmAndDelete(newFarmGuid: string) {
    setIsDeleting(true);
    try {
      await deleteFarmMutation.mutateAsync(farm.guid);
      await switchFarm(newFarmGuid);
      mixpanel.track(AnalyticsEvents.FARM_DELETED);
      navigate(`/farm/${newFarmGuid}`);
    } finally {
      setIsDeleting(false);
      setSelectFarmDialogOpen(false);
    }
  }

  async function performDelete() {
    setIsDeleting(true);
    try {
      await deleteFarmMutation.mutateAsync(farm.guid);
      mixpanel.track(AnalyticsEvents.FARM_DELETED);
      navigate("/");
    } finally {
      setIsDeleting(false);
    }
  }
}
